// src/components/world_explorer/WorldCreationModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import worldGateway from '../../api/worldGateway';

const WorldCreationModal = ({ open, onClose, onWorldCreated }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ontologies, setOntologies] = useState([]);
  const [creationMethod, setCreationMethod] = useState('from_ontology');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'sci_fi',
    ontology: '',
    autogenerate: true
  });
  const [error, setError] = useState('');
  const [createdWorld, setCreatedWorld] = useState(null);

  const steps = ['Выбор метода', 'Настройка мира', 'Создание'];

  useEffect(() => {
    if (open) {
      loadOntologies();
      // Сброс состояния при открытии
      setActiveStep(0);
      setFormData({
        name: '',
        description: '',
        type: 'sci_fi',
        ontology: '',
        autogenerate: true
      });
      setError('');
      setCreatedWorld(null);
    }
  }, [open]);

  const loadOntologies = async () => {
    try {
      const data = await worldGateway.getAvailableOntologies();
      setOntologies(data);
    } catch (err) {
      console.error('Failed to load ontologies:', err);
      // Используем заглушки если API недоступно
      setOntologies(worldGateway.getMockOntologies());
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
    setError('');
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCreateWorld = async () => {
    setLoading(true);
    setError('');

    try {
      let result;
      
      switch (creationMethod) {
        case 'from_ontology':
          if (!formData.ontology) {
            throw new Error('Please select an ontology');
          }
          result = await worldGateway.createWorldFromOntology(
            formData.ontology,
            {
              name: formData.name,
              description: formData.description,
              autogenerate: formData.autogenerate
            }
          );
          break;
        
        case 'new':
          if (!formData.name) {
            throw new Error('World name is required');
          }
          result = await worldGateway.createWorld({
            name: formData.name,
            description: formData.description,
            type: formData.type,
            autogenerate: formData.autogenerate
          });
          break;
        
        default:
          throw new Error('Unknown creation method');
      }

      setCreatedWorld(result);
      handleNext();
      
      if (onWorldCreated) {
        onWorldCreated(result);
      }
    } catch (err) {
      console.error('World creation error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create world');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      name: '',
      description: '',
      type: 'sci_fi',
      ontology: '',
      autogenerate: true
    });
    setError('');
    setCreatedWorld(null);
    onClose();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Выберите метод создания мира
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Вы можете создать мир из готовой онтологии или начать с чистого листа
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: creationMethod === 'from_ontology' ? 2 : 1,
                    borderColor: creationMethod === 'from_ontology' ? 'primary.main' : 'divider',
                    bgcolor: creationMethod === 'from_ontology' ? 'action.hover' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => setCreationMethod('from_ontology')}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" color="primary.main">
                        🏗️
                      </Typography>
                      <Typography variant="h6">
                        Из онтологии
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Создать мир на основе готовой онтологии с предопределенной структурой галактик, звездных систем и планет
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label="Рекомендуется" size="small" color="primary" />
                      <Chip label="Структурировано" size="small" variant="outlined" />
                      <Chip label="Быстрый старт" size="small" variant="outlined" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: creationMethod === 'new' ? 2 : 1,
                    borderColor: creationMethod === 'new' ? 'primary.main' : 'divider',
                    bgcolor: creationMethod === 'new' ? 'action.hover' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => setCreationMethod('new')}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" color="secondary.main">
                        🎨
                      </Typography>
                      <Typography variant="h6">
                        Новый мир
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Создать мир с нуля с полной кастомизацией параметров. Идеально для уникальных вселенных и экспериментальных проектов
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label="Гибко" size="small" color="secondary" />
                      <Chip label="Полная кастомизация" size="small" variant="outlined" />
                      <Chip label="Экспериментально" size="small" variant="outlined" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Совет:</strong> Для начала рекомендуем использовать создание из онтологии - это обеспечит базовую структуру мира, которую можно будет расширить позже.
              </Typography>
            </Alert>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Настройка параметров мира
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Задайте основные параметры вашего нового мира
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Название мира *"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  fullWidth
                  required
                  placeholder="Например: Галактика Андромеды или Киберпространство 2077"
                  helperText="Уникальное название для вашего мира"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Описание"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Опишите ключевые особенности вашего мира: тип цивилизации, технологии, основные фракции..."
                  helperText="Необязательное поле, но поможет лучше понять контекст мира"
                />
              </Grid>

              {creationMethod === 'from_ontology' && (
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Онтология *</InputLabel>
                    <Select
                      value={formData.ontology}
                      onChange={handleInputChange('ontology')}
                      label="Онтология *"
                    >
                      {ontologies.map((ontology) => (
                        <MenuItem key={ontology.name} value={ontology.name}>
                          <Box>
                            <Typography variant="body1">
                              {ontology.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ontology.description} • {ontology.entities_count} entities • Accuracy: {(ontology.scientific_accuracy * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Выберите шаблон онтологии, который определит базовую структуру вашего мира
                  </Typography>
                </Grid>
              )}

              {creationMethod === 'new' && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Тип мира</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={handleInputChange('type')}
                      label="Тип мира"
                    >
                      <MenuItem value="sci_fi">🚀 Научная фантастика</MenuItem>
                      <MenuItem value="fantasy">🧙 Фэнтези</MenuItem>
                      <MenuItem value="cyberpunk">🤖 Киберпанк</MenuItem>
                      <MenuItem value="post_apocalyptic">💀 Постапокалипсис</MenuItem>
                      <MenuItem value="realistic">🌍 Реалистичный</MenuItem>
                      <MenuItem value="alternate_history">⏳ Альтернативная история</MenuItem>
                      <MenuItem value="space_opera">🎭 Космическая опера</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Тип мира влияет на доступные технологии, магические системы и общую атмосферу
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Режим генерации</InputLabel>
                  <Select
                    value={formData.autogenerate}
                    onChange={handleInputChange('autogenerate')}
                    label="Режим генерации"
                  >
                    <MenuItem value={true}>
                      <Box>
                        <Typography variant="body1">🤖 Автоматическая генерация</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Система автоматически создаст галактики, звездные системы и планеты
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value={false}>
                      <Box>
                        <Typography variant="body1">✍️ Ручная настройка</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Только базовая структура, вы добавите контент позже вручную
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {formData.autogenerate 
                    ? 'ИИ создаст реалистичную вселенную на основе выбранных параметров' 
                    : 'Вы сможете детально настроить каждый аспект мира позже'
                  }
                </Typography>
              </Grid>
            </Grid>

            {/* Предпросмотр выбранных параметров */}
            <Card variant="outlined" sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Предпросмотр мира
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Название:
                    </Typography>
                    <Typography variant="body1">
                      {formData.name || <em>Не задано</em>}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Тип создания:
                    </Typography>
                    <Typography variant="body1">
                      {creationMethod === 'from_ontology' ? 'Из онтологии' : 'Новый мир'}
                    </Typography>
                  </Grid>
                  {creationMethod === 'from_ontology' && formData.ontology && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Онтология:
                      </Typography>
                      <Typography variant="body1">
                        {ontologies.find(o => o.name === formData.ontology)?.title || formData.ontology}
                      </Typography>
                    </Grid>
                  )}
                  {creationMethod === 'new' && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Тип мира:
                      </Typography>
                      <Typography variant="body1">
                        {formData.type}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Генерация:
                    </Typography>
                    <Typography variant="body1">
                      {formData.autogenerate ? 'Автоматическая' : 'Ручная'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            {loading ? (
              <Box sx={{ py: 4 }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Создание мира...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Это может занять несколько минут. Пожалуйста, не закрывайте окно.
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Chip label="Инициализация структуры" color="primary" variant="outlined" />
                  <Chip label="Генерация галактик" color="primary" variant="outlined" sx={{ ml: 1 }} />
                  <Chip label="Создание систем" color="primary" variant="outlined" sx={{ ml: 1 }} />
                </Box>
              </Box>
            ) : createdWorld ? (
              <Box>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'success.light',
                  color: 'success.contrastText',
                  mx: 'auto',
                  mb: 2
                }}>
                  <Typography variant="h4">✓</Typography>
                </Box>
                
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="h6">
                    Мир успешно создан!
                  </Typography>
                </Alert>
                
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {createdWorld.name}
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          ID мира:
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace">
                          {createdWorld.id}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Статус:
                        </Typography>
                        <Chip 
                          label={createdWorld.status} 
                          size="small" 
                          color={
                            createdWorld.status === 'active' ? 'success' : 
                            createdWorld.status === 'creating' ? 'warning' : 'default'
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Создан:
                        </Typography>
                        <Typography variant="body2">
                          {new Date(createdWorld.created_at).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Alert severity="info">
                  <Typography variant="body2">
                    Теперь вы можете исследовать свой новый мир в обозревателе. 
                    {createdWorld.status === 'creating' && ' Генерация контента продолжится в фоновом режиме.'}
                  </Typography>
                </Alert>
              </Box>
            ) : null}
          </Box>
        );

      default:
        return null;
    }
  };

  const getStepDisabled = () => {
    if (activeStep === 1) {
      if (!formData.name) return true;
      if (creationMethod === 'from_ontology' && !formData.ontology) return true;
    }
    return false;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          🎪 Создание нового мира
        </Typography>
        <Stepper activeStep={activeStep} sx={{ mt: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={activeStep === 0 ? handleClose : handleBack}
          disabled={loading}
        >
          {activeStep === 0 ? 'Отмена' : 'Назад'}
        </Button>
        
        {activeStep < steps.length - 1 && (
          <Button 
            onClick={handleNext} 
            variant="contained"
            disabled={getStepDisabled()}
          >
            Далее
          </Button>
        )}
        
        {activeStep === steps.length - 2 && (
          <Button 
            onClick={handleCreateWorld} 
            variant="contained"
            disabled={getStepDisabled() || loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            Создать мир
          </Button>
        )}
        
        {activeStep === steps.length - 1 && createdWorld && (
          <Button 
            onClick={handleClose} 
            variant="contained"
            color="success"
          >
            Исследовать мир
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WorldCreationModal;